import { useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Discounts } from '@prisma/client';
import { toast } from 'react-toastify';
import SectionDivider from '../SectionDivider';
import SettingsSection from '../SettingsSection';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  BusinessTaxRatesPayload,
  businessTaxRatesSchema,
  PERCENT_MAX_TAX_DECIMAL_PLACES,
  RATE_MAX_TAX_DECIMAL_PLACES,
} from '../../../types/businesses';
import { updateBusinessTaxRates } from '../../../services/business';
import { DiscountPayload, discountSchema } from '../../../types/discounts';
import { getBusinessCurrency } from '../../../helpers/businesses';
import useFeatureGuard from '../../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../../types/subscriptions';
import useCreateDiscount from '../../../hooks/discounts/useCreateDiscount';
import useGetDiscounts from '../../../hooks/discounts/useGetDiscounts';
import useUpdateDiscount from '../../../hooks/discounts/useUpdateDiscount';
import SettingsGridRow from '../SettingsGridRow';
import { convertDiscountAmountToDisplay } from '../../../helpers/discounts';
import {
  isUserAdminOrManager,
  roundToMaxDecimalPlaces,
} from '../../../utils/global/helper';

function TaxDiscountsSettings() {
  const { user, reloadUser } = useUser<LoadedUser>();

  const [form, setForm] = useState<FormData | null>(null);

  const currency = getBusinessCurrency(user?.business);

  const {
    data: discounts,
    isLoading: isLoadingDiscounts,
    isFetching: isFetchingDiscounts,
    refetch,
  } = useGetDiscounts();

  const onDiscountSuccess = async () => {
    toast.success('Discount saved!');
    await refetch();
    setForm(null);
  };

  const onDiscountError = (error?: string) => {
    toast.error(error);
  };

  const { mutateAsync: createDiscount } = useCreateDiscount({
    onSuccess: onDiscountSuccess,
    onError: onDiscountError,
  });
  const { mutateAsync: updateDiscount } = useUpdateDiscount({
    onSuccess: onDiscountSuccess,
    onError: onDiscountError,
  });

  const hasDiscountsFeature = useFeatureGuard(Feature.DISCOUNTS);
  const showDiscounts =
    user?.business?.type === 'STUDIO' &&
    isUserAdminOrManager(user) &&
    hasDiscountsFeature;

  const serviceTaxRate = user?.business?.businessTaxRates?.serviceTaxRate || 0;
  const productTaxRate = user?.business?.businessTaxRates?.productTaxRate || 0;

  const onTaxSuccess = async () => {
    await reloadUser();
    setForm(null);
  };

  const taxRatesForm: FormData<BusinessTaxRatesPayload> = {
    initialValues: {
      serviceTaxRate: roundToMaxDecimalPlaces(
        serviceTaxRate * 100,
        PERCENT_MAX_TAX_DECIMAL_PLACES,
      ),
      productTaxRate: roundToMaxDecimalPlaces(
        productTaxRate * 100,
        PERCENT_MAX_TAX_DECIMAL_PLACES,
      ),
    },
    title: 'Edit Tax Rates',
    inputRows: [
      [
        {
          label: 'Service Tax Rate',
          inputType: 'number',
          name: 'serviceTaxRate',
          min: 0,
          max: 100,
          suffix: '%',
          step: 0.001,
        },
        {
          label: 'Product Tax Rate',
          inputType: 'number',
          name: 'productTaxRate',
          min: 0,
          max: 100,
          suffix: '%',
          step: 0.001,
        },
      ],
    ],
    validationSchema: businessTaxRatesSchema,
    onSubmit: async values => {
      const body = await businessTaxRatesSchema.noUnknown().validate(values, {
        stripUnknown: true,
      });
      const payload = {
        serviceTaxRate: roundToMaxDecimalPlaces(
          body.serviceTaxRate / 100,
          RATE_MAX_TAX_DECIMAL_PLACES,
        ),
        productTaxRate: roundToMaxDecimalPlaces(
          body.productTaxRate / 100,
          RATE_MAX_TAX_DECIMAL_PLACES,
        ),
      };
      await updateBusinessTaxRates(user.business.id, payload);
      await onTaxSuccess();
    },
  };

  const getDiscountForm = (
    discount?: Discounts,
  ): FormData<DiscountPayload> => ({
    initialValues: {
      name: discount?.name || '',
      type: discount?.type || 'PERCENTAGE',
      amount: discount ? convertDiscountAmountToDisplay(discount) : 0,
      payer: discount?.payer || 'SPLIT',
      code: discount?.code || '',
      active: discount?.active ?? true,
    },
    inputRows: currentValues => [
      [
        {
          name: 'name',
          inputType: 'text',
          label: 'Name',
          placeholder: 'Enter discount name',
        },
      ],
      [
        {
          name: 'type',
          inputType: 'select',
          options: [
            { label: 'Percentage', value: 'PERCENTAGE' },
            { label: 'Fixed Amount', value: 'AMOUNT' },
          ],
          autoWidth: false,
          tooltip:
            'Select the type of discount. If you select "Percentage", the discount will be a percentage of the total price. If you select "Fixed Amount", the discount will be a fixed amount.',
          label: 'Type',
        },
        {
          name: 'amount',
          inputType: 'number',
          label: currentValues.type === 'PERCENTAGE' ? 'Percentage' : 'Amount',
          placeholder: 'Discount amount',
          min: 0,
          max: currentValues.type === 'PERCENTAGE' ? 100 : undefined,
          step: currentValues.type === 'PERCENTAGE' ? 0.01 : 1,
          suffix: currentValues.type === 'PERCENTAGE' ? '%' : currency,
        },
      ],
      [
        {
          name: 'payer',
          inputType: 'select',
          options: [
            { label: 'Business', value: 'BUSINESS' },
            { label: 'Employee', value: 'EMPLOYEE' },
            { label: 'Split', value: 'SPLIT' },
          ],
          autoWidth: false,
          label: 'Discount Payer',
          tooltip:
            'When a discount is applied to a checkout, should the amount of the discount be taken from the shop payout, the artist payout, or split based on the commission rate?',
        },
        {
          name: 'code',
          inputType: 'text',
          label: 'Discount Code',
          placeholder: 'Optional',
          tooltip: `Enter a code that customers can use to apply the discount. If you leave this field empty, the discount will not be available to customers and can only be used internally. Example: "SUMMER15"`,
        },
      ],
      [
        {
          name: 'active',
          inputType: 'checkbox',
          label: 'Active',
        },
      ],
    ],
    onSubmit: async values => {
      if (discount) {
        await updateDiscount({
          discountId: discount.id,
          payload: values,
        });
      } else {
        await createDiscount(values);
      }
    },
    title: discount ? 'Edit Discount' : 'Create Discount',
    validationSchema: discountSchema,
    submitButtonText: discount ? 'Update' : 'Create',
  });

  return (
    <>
      <SettingsSection
        title="Sales Tax"
        subtitle="Set up sales tax through Porter. Sales tax will automatically be calculated and added to charges collected through Porter. It is your responsibility to review and file your taxes on time. Consult your tax advisor to ensure taxes are applied correctly."
        action={{
          type: 'button',
          text: 'Edit',
          onClick: () => {
            setForm(taxRatesForm);
          },
        }}
        rows={[
          {
            title: 'Service Tax Rate',
            label: `${roundToMaxDecimalPlaces(serviceTaxRate * 100, 3)}%`,
            tooltip:
              'Add a service tax rate that will be applied to all tattoo and other service appointment checkouts',
          },
          {
            title: 'Product Tax Rate',
            label: `${roundToMaxDecimalPlaces(productTaxRate * 100, 3)}%`,
            tooltip:
              'Add a product tax rate for any retail items or merchandise you sell through the Checkout tab',
          },
        ]}
      />

      {showDiscounts && (
        <>
          <SectionDivider />

          <SettingsSection
            title="Discounts"
            subtitle="Create discounts that can be applied to Porter checkouts."
            action={{
              type: 'button',
              text: 'Add New Discount',
              onClick: () => {
                setForm(getDiscountForm());
              },
            }}
            isLoading={isFetchingDiscounts}
            rows={
              isLoadingDiscounts
                ? [
                    {
                      id: 'loading',
                      component: (
                        <Box
                          width="100%"
                          display="flex"
                          alignItems="center"
                          justifyContent="center">
                          <CircularProgress color="primary" />
                        </Box>
                      ),
                    },
                  ]
                : discounts?.length
                ? discounts?.map(discount => ({
                    id: discount.id,
                    component: (
                      <SettingsGridRow
                        cells={[
                          {
                            id: 'name',
                            text: (
                              <>
                                {discount.name || 'Unnamed Discount'}{' '}
                                {discount.code && (
                                  <Typography
                                    variant="inherit"
                                    component="span">
                                    (
                                    <Typography
                                      variant="inherit"
                                      component="span"
                                      sx={{
                                        textDecoration: 'underline',
                                      }}>
                                      {discount.code}
                                    </Typography>
                                    )
                                  </Typography>
                                )}
                              </>
                            ),
                          },
                          {
                            id: 'discount',
                            text: `${
                              discount.type === 'PERCENTAGE'
                                ? `${convertDiscountAmountToDisplay(discount)}%`
                                : `${
                                    currency === 'USD' ? '$' : ''
                                  }${convertDiscountAmountToDisplay(discount)}${
                                    currency !== 'USD' ? ` ${currency}` : ''
                                  }`
                            } off`,
                          },
                          {
                            id: 'payer',
                            text:
                              discount.payer === 'SPLIT'
                                ? 'Discount split based on commission'
                                : discount.payer === 'EMPLOYEE'
                                ? 'Discount taken from artist payout'
                                : 'Discount taken from shop payout',
                          },
                        ]}
                      />
                    ),
                    action: {
                      type: 'button',
                      text: 'Edit',
                      onClick: () => {
                        setForm(getDiscountForm(discount));
                      },
                    },
                  }))
                : [
                    {
                      id: 'empty',
                      component: (
                        <Typography variant="body2">
                          Add your first discount to start applying discounts to
                          Porter checkouts!
                        </Typography>
                      ),
                    },
                  ]
            }
          />
        </>
      )}

      {form && (
        <FormSmallModal
          form={form}
          onClose={() => setForm(null)}
          open={!!form}
        />
      )}
    </>
  );
}

export default TaxDiscountsSettings;
